// Landing page constants and shared data
import { Car, Baby, Dog, Briefcase, Lightbulb, Wind, Home, Package, AlertTriangle, Sun } from "lucide-react";

// Custom icons
const MotorcycleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="5" cy="17" r="3"/>
    <circle cx="19" cy="17" r="3"/>
    <path d="M5 17H3V13L7 9H10L14 4H19V9L17 11H14"/>
    <path d="M10 17L14 11"/>
  </svg>
);

const HelmetIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2C6.48 2 2 6.48 2 12v4c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2v-4c0-5.52-4.48-10-10-10z"/>
    <path d="M6 12h12"/>
    <path d="M12 2v10"/>
  </svg>
);

const WalletIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/>
    <path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/>
    <path d="M18 12a2 2 0 0 0 0 4h4v-4Z"/>
  </svg>
);

export const WHATSAPP_NUMBER = "51921959407";
export const WHATSAPP_MESSAGE = "Hola! Quiero comprar un código QR AvisoCar. Por favor contáctenme.";

// Updated reference images from user
export const IMAGES = {
  carRear1: "https://customer-assets.emergentagent.com/job_avisorescue/artifacts/tyuus00y_image.png",
  carRear2: "https://customer-assets.emergentagent.com/job_avisorescue/artifacts/4h32n5si_image.png",
  dogs: "https://customer-assets.emergentagent.com/job_avisorescue/artifacts/zc8yy1a2_image.png",
  kids: "https://customer-assets.emergentagent.com/job_avisorescue/artifacts/rgwz1w8k_image.png",
  objects: "https://customer-assets.emergentagent.com/job_avisorescue/artifacts/qpojhptl_image.png",
  stickers: "https://customer-assets.emergentagent.com/job_avisorescue/artifacts/yi2fdim0_image.png"
};

export const getCategoryModals = () => ({
  vehiculos: {
    title: "Vehículos",
    subtitle: "Sticker QR para tu auto",
    image: IMAGES.carRear1,
    color: "from-blue-500 to-blue-600",
    icon: Car,
    bullets: [
      "Alguien escanea el QR en tu luneta trasera",
      "Te llaman o envían mensaje privado",
      "Recibes aviso inmediato en tu celular"
    ]
  },
  ninos: {
    title: "Niños",
    subtitle: "Pulsera QR de seguridad",
    image: IMAGES.kids,
    color: "from-pink-500 to-rose-500",
    icon: Baby,
    bullets: [
      "Pulsera QR con diseño colorido para niños",
      "Si se pierde, cualquiera puede escanear",
      "Contactan a los padres sin ver su número"
    ]
  },
  mascotas: {
    title: "Mascotas",
    subtitle: "Placa QR para el collar",
    image: IMAGES.dogs,
    color: "from-amber-500 to-orange-500",
    icon: Dog,
    bullets: [
      "Placa QR resistente para el collar",
      "Quien encuentre a tu mascota escanea",
      "Te avisan de forma privada y segura"
    ]
  },
  objetos: {
    title: "Objetos",
    subtitle: "Sticker QR adhesivo",
    image: IMAGES.objects,
    color: "from-purple-500 to-violet-600",
    icon: Briefcase,
    bullets: [
      "Sticker QR en maletas, mochilas, equipos",
      "Si se pierde, quien lo encuentra escanea",
      "Te contactan para devolvértelo"
    ]
  }
});

// LEGACY / NO RUNTIME: el FAQ público se sirve desde i18n (`faq9.*`) con precios
// inyectados por el plan publicado. Esta lista ya no se renderiza; no editar precios aquí.
export const FAQS = [
  {
    question: "¿Necesito descargar alguna app?",
    answer: "Quien escanea tu QR solo usa la cámara de su celular, sin instalar nada. Eso sí: para RECIBIR AVISOS como propietario es obligatorio instalar AvisoCar en la pantalla de inicio de tu teléfono — toma 10 segundos.",
    link: { to: "/ayuda/usar-en-navegador", label: "Ver la guía de instalación paso a paso" }
  },
  {
    question: "¿Se ve mi número de teléfono?",
    answer: "Nunca. Quien escanea tu QR jamás ve tu número de teléfono: los mensajes y las llamadas ocurren dentro de AvisoCar."
  },
  {
    question: "¿Cuánto cuesta AvisoCar?",
    answer: "La membresía AvisoCar cuesta S/50 por 6 meses o S/80 por 1 año (ahorras S/20). Incluye mensajes privados, llamadas privadas, notificaciones push instantáneas, contactos de confianza y bloqueo de contactos molestos."
  },
  {
    question: "¿Funciona en todo Perú?",
    answer: "Sí, AvisoCar funciona en todo el territorio peruano. Solo necesitas conexión a internet para recibir avisos."
  },
  {
    question: "¿Cómo coloco el sticker en mi auto?",
    answer: "El sticker es adhesivo resistente al agua. Recomendamos colocarlo en la luneta trasera, visible desde afuera."
  }
];

// V8 FIX: Real life cases with REALISTIC Peruvian scenarios
// Removed "Audífonos perdidos", fixed wording, added "Cartera perdida"
export const REAL_LIFE_CASES = [
  { icon: Lightbulb, title: "Luces encendidas", color: "from-yellow-500 to-amber-500" },
  { icon: Wind, title: "Ventanas abiertas", color: "from-cyan-500 to-blue-500" },
  { icon: Home, title: "Auto bloqueando cochera", color: "from-red-500 to-rose-500" },
  { icon: Baby, title: "Niño perdido en parque", color: "from-pink-500 to-rose-500" },
  // FIXED: "encontrada" → "perdida"
  { icon: Dog, title: "Mascota perdida", color: "from-amber-500 to-orange-500" },
  // FIXED: Added "perdida"
  { icon: Package, title: "Maleta perdida en aeropuerto", color: "from-blue-500 to-indigo-500" },
  { icon: MotorcycleIcon, title: "Moto mal estacionada", color: "from-purple-500 to-violet-500" },
  { icon: HelmetIcon, title: "Casco con QR AvisoCar", color: "from-green-500 to-emerald-500" },
  // NEW: Cartera perdida (replaces Audífonos perdidos)
  { icon: WalletIcon, title: "Cartera perdida", color: "from-slate-500 to-slate-600" },
  { icon: AlertTriangle, title: "Paquete entregado por error", color: "from-orange-500 to-red-500" },
  { icon: Car, title: "Auto mal estacionado", color: "from-blue-600 to-blue-700" },
  { icon: Sun, title: "Emergencia vial", color: "from-red-600 to-rose-600" }
];
